"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useRouter } from "next/navigation"
import { useAuth } from "@/hooks/use-auth"
import { SessionHeader } from "@/components/dashboard/practice/session/header"
import { QuestionInfoBar } from "@/components/dashboard/practice/session/info-bar"
import { MobileToolbar } from "@/components/dashboard/practice/session/mobile-toolbar"
import { QuestionDisplay } from "@/components/dashboard/practice/session/question-display"
import { AnswerSection } from "@/components/dashboard/practice/session/answer-section"
import { SessionFooter } from "@/components/dashboard/practice/session/footer"
import { SplitPane } from "@/components/ui/split-pane"
import { MathRenderer } from "@/components/ui/math-renderer"
import { DEMO_QUESTIONS } from "@/components/landing/demo-questions"
import { QuestionResponse } from "@/services/practice"
import { Loader2, Zap, Server, BrainCircuit, CheckCircle2 } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

import Link from "next/link"

// Helper to map demo question to QuestionResponse
const mapToQuestionResponse = (demoQ: any): QuestionResponse => {
    const optionsMap: Record<string, string> = {};
    const letters = ['A', 'B', 'C', 'D'];
    demoQ.options.forEach((opt: string, idx: number) => {
        if (idx < 4) optionsMap[letters[idx]] = opt;
    });

    return {
        id: demoQ.id,
        questionId: `demo-${demoQ.id}`,
        providerCode: "DEMO",
        questionType: "mcq",
        domainName: demoQ.domain,
        difficulty: demoQ.difficulty,
        stem: demoQ.text,
        mcqOptions: optionsMap,
        mcqAnswer: letters[demoQ.correctAnswer],
        explanation: "This is a demo. Sign up for full access to detailed expert explanations for every question!",
        userContext: {
            isMastered: false,
            attemptCount: 0,
            isMarked: false
        }
    };
}

export default function DemoPage() {
    const router = useRouter()
    const { user } = useAuth()

    // State
    const [currentIndex, setCurrentIndex] = useState(0)
    const [answers, setAnswers] = useState<Record<string, string>>({})
    const [loading, setLoading] = useState(true)
    const [questions, setQuestions] = useState<QuestionResponse[]>([])

    // Feedback state
    const [questionStates, setQuestionStates] = useState<Record<string, {
        submitted: boolean;
        isCorrect: boolean;
        correctAnswer?: string;
        explanation?: string
    }>>({})

    // Timers
    const [elapsedDisplay, setElapsedDisplay] = useState(0)
    const [startTime, setStartTime] = useState(Date.now())
    const [isFinished, setIsFinished] = useState(false)

    // UI Toggles
    const [showInfo, setShowInfo] = useState(true)

    // Scroll refs
    const singleColumnRef = useRef<HTMLDivElement>(null)
    const splitLeftRef = useRef<HTMLDivElement>(null)
    const splitRightRef = useRef<HTMLDivElement>(null)
    const splitPaneRef = useRef<HTMLDivElement>(null)

    // Initialize questions
    useEffect(() => {
        const mapped = DEMO_QUESTIONS.map(mapToQuestionResponse)
        setQuestions(mapped)
        // Loading is handled by InitializingOverlay
        setStartTime(Date.now())
    }, [])

    // Timer tick
    useEffect(() => {
        if (isFinished || loading) return
        const timer = setInterval(() => {
            setElapsedDisplay(Math.floor((Date.now() - startTime) / 1000))
        }, 1000)
        return () => clearInterval(timer)
    }, [startTime, isFinished, loading])

    // Reset scroll on question change
    useEffect(() => {
        if (singleColumnRef.current) singleColumnRef.current.scrollTop = 0
        if (splitLeftRef.current) splitLeftRef.current.scrollTop = 0
        if (splitRightRef.current) splitRightRef.current.scrollTop = 0
        if (splitPaneRef.current) splitPaneRef.current.scrollTop = 0
    }, [currentIndex])

    const currentQuestion = questions[currentIndex]

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1)
        } else {
            setIsFinished(true)
        }
    }

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1)
        }
    }

    const handleAnswerChange = (val: string) => {
        if (!currentQuestion) return
        setAnswers(prev => ({
            ...prev,
            [currentQuestion.id.toString()]: val
        }))
        // Clear submitted state if they change answer (optional, mimicking real app behavior)
        if (questionStates[currentQuestion.id.toString()]?.submitted) {
            setQuestionStates(prev => {
                const newState = { ...prev }
                delete newState[currentQuestion.id.toString()]
                return newState
            })
        }
    }

    const handleCheck = async () => {
        if (!currentQuestion) return
        const qId = currentQuestion.id.toString()
        const answer = answers[qId]

        if (!answer) {
            toast.error("Please select an answer")
            return
        }

        const isCorrect = answer === currentQuestion.mcqAnswer

        setQuestionStates(prev => ({
            ...prev,
            [qId]: {
                submitted: true,
                isCorrect: isCorrect,
                correctAnswer: currentQuestion.mcqAnswer,
                explanation: currentQuestion.explanation
            }
        }))
    }

    const handleIntroComplete = useCallback(() => {
        setLoading(false)
        setStartTime(Date.now())
    }, [])

    const handleFinish = () => {
        setIsFinished(true)
    }

    // Results Overlay
    if (isFinished) {
        const correctCount = Object.values(questionStates).filter(s => s.isCorrect).length
        const total = questions.length

        return (
            <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center p-4 animate-in fade-in duration-300">
                <Card className="w-full max-w-md border-2 border-primary shadow-2xl">
                    <CardContent className="pt-6 flex flex-col items-center text-center space-y-6">
                        <div className="rounded-full bg-primary/10 p-6">
                            <Zap className="w-12 h-12 text-primary fill-primary" />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-3xl font-bold">Demo Complete!</h2>
                            <p className="text-muted-foreground">You didn't do bad at all!</p>
                        </div>

                        <div className="flex flex-col items-center justify-center rounded-lg border bg-card p-6 w-full">
                            <span className="text-5xl font-bold mb-2">{Math.round((correctCount / total) * 100)}%</span>
                            <span className="text-sm text-muted-foreground">Accuracy Score</span>
                        </div>

                        <div className="space-y-4 w-full">
                            <p className="text-sm text-muted-foreground">
                                Detailed analytics, adaptive difficulty, and 1000+ more questions are waiting for you.
                            </p>
                            <Link href="/register" className="w-full block">
                                <Button size="lg" className="w-full font-bold text-lg h-12">
                                    Create Free Account
                                </Button>
                            </Link>
                            <Link href="/" className="w-full block">
                                <Button variant="ghost" className="w-full">
                                    Back to Home
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }



    if (loading || !currentQuestion) {
        return <InitializingOverlay onComplete={handleIntroComplete} />
    }

    const currentQState = questionStates[currentQuestion.id.toString()] || {}

    return (
        <div className="fixed inset-0 z-40 bg-background flex flex-col h-[100dvh] w-screen overflow-hidden">
            {/* Reusing actual SessionHeader but with mocked props where needed */}
            <SessionHeader
                title={`DEMO: ${currentQuestion.domainName}`}
                question={currentQuestion}
                elapsedTime={elapsedDisplay}
                onToggleInfo={() => setShowInfo(!showInfo)}
                isInfoVisible={showInfo}
                activeTool={'dictionary'}
                onToolChange={() => { }}
                showSolutionButton={false}
                onShowSolution={() => { }}
            />

            <MobileToolbar
                question={currentQuestion}
                activeTool={'dictionary'}
                onToolChange={() => { }}
                userRole={user?.role}
                title={`DEMO: ${currentQuestion.domainName}`}
            />

            {showInfo && (
                <QuestionInfoBar
                    question={currentQuestion}
                    currentIndex={currentIndex}
                    total={questions.length}
                />
            )}

            <main className="flex-1 overflow-hidden relative">
                <div className="h-full container mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 max-w-[1600px]">
                    {/* Always using SplitLayout for consistency with screenshot style if stimulus exists or just generic container */}
                    {/* Since demo questions have short text, we might treat them as single column mostly, but let's stick to the logic */}
                    {/* Actually most SAT reading Qs have 'stimulus' (the text) and 'stem' (the question).
                        My DEMO_QUESTIONS have 'text' which is the stimulus usually. 
                        Wait, mapping: stem = text in current map. 
                        Real SAT has Stimulus (Paragraph) + Stem (Question).
                        My demo questions text is "While the research...". That's the stimulus. The question is implied?
                        Ah, look at Q1: "While the research...". That is the stimulus. 
                        The options are just options. Ideally there is a question stem like "Which choice completes..."
                        Looking at my data: "Which choice best describes..." (Q3).
                        
                        Okay, for the purpose of the demo, I mapped `stem: demoQ.text`. 
                        If `stimulus` is missing, `PracticeSessionPage` renders `SingleColumn`.
                        So i will stick to SingleColumn (Bottom logic in original file) for now unless I split the text manually.
                    */}

                    <div ref={singleColumnRef} className="h-full overflow-y-auto max-w-4xl mx-auto pt-2 sm:pt-3 md:pt-4 pb-16 sm:pb-20">
                        <div className="mb-4 sm:mb-6 md:mb-8 p-1">
                            <div className="text-lg sm:text-xl font-medium leading-relaxed font-serif">
                                <MathRenderer content={currentQuestion.stem} />
                            </div>
                        </div>

                        <AnswerSection
                            question={currentQuestion}
                            selectedAnswer={answers[currentQuestion.id.toString()] || null}
                            onMainAnswerChange={handleAnswerChange}
                            submitted={currentQState?.submitted}
                            isCorrect={currentQState?.isCorrect}
                            correctAnswer={currentQState?.correctAnswer}
                            explanation={currentQState?.explanation}
                            isMarked={false}
                            onMark={() => toast.info("Sign up to save marked questions!")}
                            onReport={() => toast.info("This is a demo question.")}
                        />
                    </div>
                </div>
            </main>

            <SessionFooter
                currentIndex={currentIndex}
                total={questions.length}
                onNext={handleNext}
                onPrev={handlePrev}
                onJump={setCurrentIndex}
                onCheck={handleCheck}
                onFinish={handleFinish}
                onExit={() => router.push('/')}
                isChecking={false}
                isCorrect={currentQState?.isCorrect}
                getQuestionStatus={(index) => {
                    const q = questions[index]
                    const state = questionStates[q.id.toString()]
                    return {
                        isMastered: false,
                        isTried: !!state,
                        isMarked: false
                    }
                }}
            />

        </div>
    )
}


function InitializingOverlay({ onComplete }: { onComplete: () => void }) {
    useEffect(() => {
        // Just a brief pause to smooth the transition, no fake steps
        const timer = setTimeout(onComplete, 800)
        return () => clearTimeout(timer)
    }, [onComplete])

    return (
        <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center p-4">
            <div className="flex flex-col items-center space-y-4 animate-in fade-in duration-500">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center relative">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h2 className="text-xl font-medium text-muted-foreground">Setting up your demo...</h2>
            </div>
        </div>
    )
}
